/* global globalThis */
import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from "react";
import PropTypes from "prop-types";
import { format } from "date-fns";
import { useLocation } from "react-router-dom";
import {
  Box,
  CssBaseline,
  Button,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Table,
  TableBody,
  Stack,
  IconButton,
  Dialog,
  DialogContent,
  Typography,
  Tooltip,
  useMediaQuery,
  useTheme,
  styled,
} from "@mui/material";

import {
  Download as DownloadIcon,
  Calendar as CalendarIcon,
} from "lucide-react";
import {
  ArrowDropUp as ArrowDropUpIcon,
  ArrowDropDown as ArrowDropDownIcon,
} from "@mui/icons-material";
import { LocalizationProvider, DesktopDatePicker } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import PageHeader from "../../components/common/PageHeader";
import CloseIcon from "@mui/icons-material/Close";
import {
  fetchInvoicingReporting,
  exportInvoicingReporting,
} from "../../api/reporting";
import { useLoading } from "../../context/LoadingContext";

// --- Helpers & Small Components ---

const MainContent = styled("div")(() => ({
  flexGrow: 1,
  padding: 0,
  margin: 0,
  width: "100%",
  minHeight: "80vh",
  backgroundColor: "#F7F7FB",
  overflow: "hidden",
}));

function getScreenInches() {
  const { width, height } = globalThis.screen;
  const dpi = globalThis.devicePixelRatio * 96;
  const widthInInches = width / dpi;
  const heightInInches = height / dpi;
  return Math.hypot(widthInInches, heightInInches);
}

const SECTIONS = {
  members: {
    description: "Search and manage member information",
  },
  providers: {
    title: "Providers",
    description: "Search and manage provider information",
  },
  pharmacies: {
    title: "Pharmacies",
    description: "Search and manage pharmacy information",
  },
  programs: {
    title: "Programs",
    description: "Search and manage program information",
  },
};

const BREADCRUMBS = [
  {
    label: "Reporting",
    path: "/reporting/invoice-reporting",
  },
  {
    label: "Invoice Reporting",
    path: "/reporting/invoice-reporting",
  },
];

const formatClientName = (raw) => {
  if (!raw || typeof raw !== "string") return "N/A";
  let s = raw.trim();
  s = s.replaceAll(/\s+/g, " ");
  // Replace all whitespace-comma-whitespace with ', ' using split/join to avoid regex ReDoS
  s = s
    .split(",")
    .map((part) => part.trim())
    .join(", ");
  s = s.replace(/,\s*Inc\.?$/i, ", Inc");

  const acronymSet = new Set(["LLC", "LP", "LLP"]);
  s = s
    .split(/(\s+)/)
    .map((token) => {
      if (/^\s+$/.test(token)) return token;
      return token
        .split(/(,)/)
        .map((sub) => {
          if (sub === ",") return sub;
          const upper = sub.toUpperCase();
          if (acronymSet.has(upper)) return upper;
          return upper.charAt(0) + upper.slice(1).toLowerCase();
        })
        .join("");
    })
    .join("");
  return s;
};

const formatBillableEvent = (val) => {
  if (val === true) return "Yes";
  if (val === false) return "No";
  return "N/A";
};

const mapResponse = (apiRes) => {
  if (!apiRes) return [];
  const rows = apiRes.content || apiRes;
  if (!Array.isArray(rows)) return [];

  return rows.map((r, idx) => ({
    id: r.memberId || `row-${idx}`,
    originalIndex: idx,
    clientName: formatClientName(r.clientName || ""),
    memberId: r.memberId || "N/A",
    dateOfInteraction: r.dateOfInteraction || "N/A",
    programName: r.programName === "-" ? "N/A" : r.programName,
    recipientType: r.recipientType || "N/A",
    outreachOutcome: r.outreachOutcome || "N/A",
    programOutcome: r.programOutcome || "N/A",
    billableEvent: formatBillableEvent(r.billableEvent),
    billableAmount: r.billableAmount || "N/A",
  }));
};

const compareMemberIds = (av, bv) => {
  const isNumeric = (val) => /^\d+$/.test(val);
  const isAlphaNum = (val) => /^(?=.*[a-zA-Z])(?=.*\d)[a-zA-Z\d]+$/.test(val);
  const isAlpha = (val) => /^[a-zA-Z]+$/.test(val);

  const getType = (val) => {
    if (isNumeric(val)) return 0;
    if (isAlphaNum(val)) return 1;
    if (isAlpha(val)) return 2;
    return 3;
  };

  const aType = getType(av);
  const bType = getType(bv);

  if (aType !== bType) {
    return aType - bType;
  }

  if (aType === 0) {
    return Number.parseInt(av, 10) - Number.parseInt(bv, 10);
  }
  return String(av ?? "").localeCompare(String(bv ?? ""));
};

const compareDates = (av, bv) => {
  const parseTime = (val) => {
    const t = val ? Date.parse(val) : Number.NaN;
    return Number.isNaN(t) ? Number.MAX_SAFE_INTEGER : t;
  };
  const ad = parseTime(av);
  const bd = parseTime(bv);
  return ad - bd;
};

const getEffectiveDateRange = (appliedRange) => {
  const today = new Date();
  const startOfYear = new Date(today.getFullYear(), 0, 1);
  const useCustom = appliedRange.startDate && appliedRange.endDate;
  return {
    start: useCustom ? appliedRange.startDate : startOfYear,
    end: useCustom ? appliedRange.endDate : today,
  };
};

const getSortedData = (data, sortConfig) => {
  const sorted = [...data];
  const { key, direction } = sortConfig;
  const compare = (a, b) => {
    const av = a[key];
    const bv = b[key];

    if (key === "memberId") {
      return compareMemberIds(av, bv);
    }

    if (key === "dateOfInteraction") {
      return compareDates(av, bv);
    }

    const an = typeof av === "number" ? av : Number.parseFloat(av);
    const bn = typeof bv === "number" ? bv : Number.parseFloat(bv);
    const bothNumeric = !Number.isNaN(an) && !Number.isNaN(bn);
    if (bothNumeric) return an - bn;
    return String(av ?? "").localeCompare(String(bv ?? ""));
  };
  sorted.sort((a, b) => (direction === "asc" ? compare(a, b) : -compare(a, b)));
  return sorted;
};

// --- Extracted Components ---

const InvoiceExportButton = ({ totalRows, appliedDateRange, setError }) => {
  const handleExport = async () => {
    if (totalRows === 0) return;
    try {
      const { start, end } = getEffectiveDateRange(appliedDateRange);
      const params = {
        startDate: format(start, "yyyy-MM-dd"),
        endDate: format(end, "yyyy-MM-dd"),
      };
      const blob = await exportInvoicingReporting(params);

      const url = globalThis.URL.createObjectURL(blob);
      const a = document.createElement("a");

      const formatMDY = (dateStr) => {
        const d = new Date(dateStr);
        return `${String(d.getMonth() + 1).padStart(2, "0")}_${String(
          d.getDate(),
        ).padStart(2, "0")}_${d.getFullYear()}`;
      };
      const rangeLabel = `(${formatMDY(params.startDate)})-(${formatMDY(
        params.endDate,
      )})`;
      a.href = url;
      a.download = `Invoice_Reporting_${rangeLabel}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      globalThis.URL.revokeObjectURL(url);
    } catch (e) {
      console.error("Export failed", e);
      setError("Failed to export data");
    }
  };

  const hasData = totalRows > 0;
  const commonColor = hasData ? "#403C6F" : "#999893";

  return (
    <Button
      startIcon={
        <DownloadIcon
          style={{ width: "18px", height: "16px", color: commonColor }}
        />
      }
      onClick={handleExport}
      disabled={!hasData}
      sx={{
        fontSize: "14px",
        fontWeight: 500,
        lineHeight: "24px",
        letterSpacing: "0%",
        borderRadius: "20px",
        border: "1px solid #E8E8E9",
        textTransform: "none",
        backgroundColor: hasData ? "#FFFFFF" : "#EFEFEF",
        color: commonColor,
        px: 2,
        py: 0,
        display: "inline-flex",
        alignItems: "center",
        verticalAlign: "middle",
      }}
    >
      Export Data
    </Button>
  );
};

InvoiceExportButton.propTypes = {
  totalRows: PropTypes.number.isRequired,
  appliedDateRange: PropTypes.object.isRequired,
  setError: PropTypes.func.isRequired,
};

const InvoiceDateFilterControl = ({
  appliedDateRange,
  openDateDialog,
  closeDateDialog,
  setAppliedDateRange,
  setDateRange,
  setPageIndex,
  totalRows,
}) => {
  const hasDateRange = Boolean(
    appliedDateRange.startDate && appliedDateRange.endDate,
  );

  const handleClear = (e) => {
    e.stopPropagation();
    e.preventDefault();
    setAppliedDateRange({ startDate: null, endDate: null });
    setDateRange({ startDate: null, endDate: null });
    setPageIndex(0);
    closeDateDialog();
  };

  const handleClick = (e) => {
    const clearIcon = e.target.closest('[data-clear-icon="true"]');
    if (hasDateRange && clearIcon) {
      handleClear(e);
      return;
    }
    openDateDialog();
  };

  const formatDate = (date) => new Date(date).toLocaleDateString("en-US");

  const labelText = hasDateRange
    ? `${formatDate(appliedDateRange.startDate)} - ${formatDate(
        appliedDateRange.endDate,
      )}`
    : "Select Date Range";

  const ariaLabel = hasDateRange
    ? `${labelText}, click X icon to clear`
    : labelText;

  const iconColor = totalRows > 0 ? "#403C6F" : "#999893";
  const iconStyle = { width: "18px", height: "16px", color: iconColor };

  const ClearButton = (
    <Box
      component="span"
      data-clear-icon="true"
      onClick={handleClear}
      sx={{
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        p: 0,
        ml: 0.5,
        color: iconColor,
        cursor: totalRows > 0 ? "pointer" : "not-allowed",
        opacity: totalRows > 0 ? 1 : 0.5,
        "&:hover": { opacity: totalRows > 0 ? 0.7 : 0.5 },
      }}
      aria-hidden="true"
    >
      <CloseIcon style={iconStyle} />
    </Box>
  );

  return (
    <Button
      variant="outlined"
      endIcon={hasDateRange ? ClearButton : <CalendarIcon style={iconStyle} />}
      onClick={handleClick}
      aria-label={ariaLabel}
      sx={{
        fontSize: "14px",
        fontWeight: 500,
        border: "1px solid #E8E8E9",
        lineHeight: "24px",
        letterSpacing: "0%",
        borderRadius: "20px",
        textTransform: "none",
        color: "#403C6F",
        backgroundColor: "#FFFFFF",
        display: "inline-flex",
        alignItems: "center",
        verticalAlign: "middle",
      }}
    >
      {labelText}
    </Button>
  );
};

InvoiceDateFilterControl.propTypes = {
  appliedDateRange: PropTypes.object.isRequired,
  openDateDialog: PropTypes.func.isRequired,
  closeDateDialog: PropTypes.func.isRequired,
  setAppliedDateRange: PropTypes.func.isRequired,
  setDateRange: PropTypes.func.isRequired,
  setPageIndex: PropTypes.func.isRequired,
  totalRows: PropTypes.number.isRequired,
};

const InvoiceDateRangeDialog = ({
  open,
  onClose,
  dateRange,
  onDateChange,
  endDateError,
  setEndDateError,
  onApply,
}) => {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      aria-labelledby="date-range-dialog-title"
      slotProps={{
        paper: {
          sx: {
            borderRadius: "16px",
            padding: "16px",
            minWidth: 400,
            "& .MuiPickersDay-root": {
              color: "#616161 !important",
              "&.Mui-disabled": { color: "#9e9e9e !important" },
              "&.MuiPickersDay-today": {
                color: "#424242 !important",
                border: "1px solid #757575 !important",
                backgroundColor: "transparent !important",
              },
              "&:hover": {
                backgroundColor: "rgba(0, 0, 0, 0.04) !important",
              },
              "&.Mui-selected": {
                backgroundColor: "#403C6F !important",
                color: "#ffffff !important",
                "&:hover": { backgroundColor: "#322E59 !important" },
              },
            },
            "& .MuiPickersCalendarHeader-label": {
              color: "#616161 !important",
            },
          },
        },
      }}
    >
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={2}
      >
        <Typography
          id="date-range-dialog-title"
          variant="h6"
          sx={{ color: "#3D3769", fontWeight: 600 }}
        >
          Select Date Range
        </Typography>
        <IconButton
          onClick={onClose}
          size="small"
          aria-label="Close date range dialog"
        >
          <CloseIcon sx={{ color: "#3D3769" }} />
        </IconButton>
      </Box>
      <DialogContent sx={{ p: 0 }}>
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <Box
            display="flex"
            gap={2}
            mb={3}
            flexDirection={{ xs: "column", sm: "row" }}
          >
            <Box flex={1}>
              <Typography
                variant="body2"
                sx={{
                  color: "#3D3769",
                  mb: 1,
                  fontWeight: 500,
                  fontSize: "14px",
                }}
              >
                Start Date
              </Typography>
              <DesktopDatePicker
                value={dateRange.startDate}
                disableFuture
                onChange={(date) => {
                  onDateChange(date, "startDate");
                  setEndDateError(() => {
                    if (date && dateRange.endDate && dateRange.endDate < date) {
                      return "End date cannot be earlier than start date";
                    }
                    return "";
                  });
                }}
                slotProps={{
                  openPickerButton: { "aria-label": "Open start date picker" },
                  textField: {
                    size: "small",
                    fullWidth: true,
                    placeholder: "YYYY-MM-DD",
                    slotProps: {
                      input: {
                        sx: {
                          height: "40px",
                          "& input": {
                            padding: "8px 14px",
                            color: dateRange.startDate ? "#111827" : "#6B7280",
                            "&::placeholder": { color: "#6B7280", opacity: 1 },
                          },
                        },
                      },
                    },
                    sx: {
                      "& .MuiOutlinedInput-root": {
                        borderRadius: "8px",
                        backgroundColor: "#FFFFFF",
                        "& fieldset": { borderColor: "#E8E8E9" },
                        "&:hover fieldset": { borderColor: "#D0D0D0" },
                        "&.Mui-focused fieldset": { borderColor: "#E8E8E9" },
                      },
                    },
                  },
                }}
              />
            </Box>
            <Box flex={1}>
              <Typography
                variant="body2"
                sx={{
                  color: "#3D3769",
                  mb: 1,
                  fontWeight: 500,
                  fontSize: "14px",
                }}
              >
                End Date
              </Typography>
              <DesktopDatePicker
                value={dateRange.endDate}
                disableFuture
                minDate={dateRange.startDate || undefined}
                onChange={(date) => {
                  onDateChange(date, "endDate");
                  if (
                    dateRange.startDate &&
                    date &&
                    date < dateRange.startDate
                  ) {
                    setEndDateError(
                      "End date cannot be earlier than start date",
                    );
                  } else {
                    setEndDateError("");
                  }
                }}
                slotProps={{
                  openPickerButton: { "aria-label": "Open end date picker" },
                  textField: {
                    size: "small",
                    fullWidth: true,
                    placeholder: "YYYY-MM-DD",
                    error: Boolean(endDateError),
                    helperText: endDateError || "",
                    slotProps: {
                      input: {
                        sx: {
                          height: "40px",
                          "& input": {
                            padding: "8px 14px",
                            color: dateRange.endDate ? "#111827" : "#6B7280",
                            "&::placeholder": { color: "#6B7280", opacity: 1 },
                          },
                        },
                      },
                    },
                    sx: {
                      "& .MuiOutlinedInput-root": {
                        borderRadius: "8px",
                        backgroundColor: "#FFFFFF",
                        "& fieldset": { borderColor: "#E8E8E9" },
                        "&:hover fieldset": { borderColor: "#D0D0D0" },
                        "&.Mui-focused fieldset": { borderColor: "#E8E8E9" },
                      },
                    },
                  },
                }}
              />
            </Box>
          </Box>
        </LocalizationProvider>
        <Box display="flex" justifyContent="flex-start" mt={2}>
          <Button
            variant="contained"
            onClick={onApply}
            disabled={
              !(dateRange.startDate && dateRange.endDate) ||
              Boolean(endDateError)
            }
            sx={{
              backgroundColor: "#3D3769",
              borderRadius: "24px",
              textTransform: "none",
              fontWeight: 600,
              px: 4,
              py: 1,
              fontSize: "14px",
              "&:hover": { backgroundColor: "#322E59", boxShadow: "none" },
              "&.Mui-disabled": {
                backgroundColor: "#E8E8E9",
                color: "#A0A0A0",
              },
            }}
          >
            Apply
          </Button>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

InvoiceDateRangeDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  dateRange: PropTypes.object.isRequired,
  onDateChange: PropTypes.func.isRequired,
  endDateError: PropTypes.string.isRequired,
  setEndDateError: PropTypes.func.isRequired,
  onApply: PropTypes.func.isRequired,
};

const InvoiceReportingTable = ({
  loading,
  error,
  results,
  pageIndex,
  totalRows,
  computedTableHeight,
  sortedInvoiceReporting,
  sortConfig,
  onSort,
  tableContainerRef,
  isMobile,
}) => {
  if (error) {
    return (
      <p
        style={{
          color: "red",
          backgroundColor: "#ffffff",
          padding: "8px 12px",
          borderRadius: "4px",
          border: "1px solid #ffcccc",
          display: "inline-block",
        }}
      >
        {error}
      </p>
    );
  }

  if (!results.length && (!loading || pageIndex !== 0)) {
    return null;
  }

  const getSortArrows = (colKey) => (
    <span
      style={{ display: "inline-flex", flexDirection: "column", marginLeft: 4 }}
    >
      <ArrowDropUpIcon
        style={{
          fontSize: 16,
          color: "inherit",
          lineHeight: 0,
          opacity:
            sortConfig.key === colKey && sortConfig.direction === "asc"
              ? 1
              : 0.8,
        }}
      />
      <ArrowDropDownIcon
        style={{
          fontSize: 16,
          color: "inherit",
          marginTop: -8,
          lineHeight: 0,
          opacity:
            sortConfig.key === colKey && sortConfig.direction === "desc"
              ? 1
              : 0.8,
        }}
      />
    </span>
  );

  return (
    <div style={{ marginBottom: "16px", marginRight: "0px" }}>
      <TableContainer
        ref={tableContainerRef}
        tabIndex={0}
        component="section"
        aria-label="Invoice reporting table"
        sx={{
          maxHeight: computedTableHeight || "calc(100dvh - 180px)",
          overflowY: "auto",
          overflowX: "scroll",
          height: computedTableHeight || "calc(100dvh - 180px)",
          backgroundColor: "#fff",
          scrollbarGutter: "stable",
        }}
      >
        <Table
          stickyHeader
          size="small"
          sx={{
            width: isMobile ? "auto" : "100%",
            tableLayout: "auto",
            minWidth: isMobile ? "1000px" : 1370,
            "& .MuiTableCell-root": {
              color: "#403C6F",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              padding: "16px 22px",
              borderBottom: "none",
              "&:not(.MuiTableCell-head)": {
                fontWeight: 500,
                fontSize: "16px",
              },
            },
            "& .MuiTableHead-root .MuiTableCell-root": {
              position: "sticky",
              top: 0,
              zIndex: 2,
              backgroundColor: "#CFCEDA",
              padding: "12px 18px !important",
              fontStyle: "normal",
              fontWeight: 550,
              fontSize: "16px",
              lineHeight: "24px",
              letterSpacing: "0px",
            },
            "& .MuiTableBody-root .MuiTableRow-root": { height: 90 },
            "& .MuiTableCell-root:nth-of-type(1)": { minWidth: 170 },
            "& .MuiTableCell-root:nth-of-type(2)": { minWidth: 170 },
            "& .MuiTableCell-root:nth-of-type(3)": { minWidth: 170 },
            "& .MuiTableCell-root:nth-of-type(4)": { minWidth: 170 },
            "& .MuiTableCell-root:nth-of-type(5)": { minWidth: 170 },
            "& .MuiTableCell-root:nth-of-type(6)": { minWidth: 170 },
            "& .MuiTableCell-root:nth-of-type(7)": { minWidth: 220 },
            "& .MuiTableCell-root:nth-of-type(8)": { minWidth: 170 },
          }}
        >
          <TableHead>
            <TableRow
              sx={{
                backgroundColor: "#CFCEDA",
                "& .MuiTableCell-head": { borderRight: "none !important" },
              }}
            >
              {[
                { key: "clientName", label: "Client Name" },
                { key: "memberId", label: "Member ID" },
                { key: "dateOfInteraction", label: "Date of Interaction" },
                { key: "programName", label: "Program Name", width: 110 },
                { key: "recipientType", label: "Recipient Type", width: 20 },
                {
                  key: "outreachOutcome",
                  label: "Outreach Outcomes",
                  width: 20,
                },
                { key: "programOutcome", label: "Program Outcomes", width: 20 },
                { key: "billableEvent", label: "Billable Event", width: 20 },
              ].map((col) => (
                <TableCell
                  key={col.key}
                  onClick={() => onSort(col.key)}
                  sx={{
                    cursor: "pointer",
                    color: "#403C6F",
                    fontWeight: 700,
                    fontSize: "14px",
                    width: col.width || "auto",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  <span style={{ display: "flex", alignItems: "center" }}>
                    {col.label} {getSortArrows(col.key)}
                  </span>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedInvoiceReporting.map((member) => (
              <TableRow
                key={`${member.id}-${member.originalIndex}`}
                hover
                sx={{ "&:hover": { backgroundColor: "#F9F9F9 !important" } }}
              >
                <TableCell sx={{ fontWeight: 500 }}>
                  <Tooltip
                    title={member.clientName || ""}
                    placement="bottom-start"
                    arrow
                    describeChild
                    disableInteractive
                    slotProps={{
                      popper: {
                        sx: {
                          "& .MuiTooltip-tooltip": {
                            backgroundColor: "#1c1c1f",
                            color: "#FFFFFF",
                            margin: 0,
                            fontSize: "12px",
                            lineHeight: 1.3,
                          },
                          "& .MuiTooltip-arrow": {
                            color: "#1c1c1f",
                            margin: 0,
                            left: "50% !important",
                            transform: "translateX(-50%) !important",
                          },
                        },
                      },
                      tooltip: {
                        sx: {
                          margin: 0,
                          marginTop: "0px !important",
                          padding: "4px 8px",
                        },
                      },
                    }}
                  >
                    <span
                      style={{
                        fontWeight: 500,
                        fontSize: "16px",
                        lineHeight: "24px",
                        color: "#4b3f72",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        display: "inline-block",
                        maxWidth: "100%",
                      }}
                    >
                      {member.clientName}
                    </span>
                  </Tooltip>
                </TableCell>
                <TableCell sx={{ fontWeight: 500, fontSize: "16px" }}>
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: 4,
                      minWidth: "120px",
                    }}
                  >
                    {member.memberId}
                  </div>
                </TableCell>
                <TableCell sx={{ fontWeight: 500, fontSize: "16px" }}>
                  {member.dateOfInteraction}
                </TableCell>
                <TableCell
                  sx={{ fontWeight: 500, fontSize: "16px", width: 110 }}
                >
                  {member.programName}
                </TableCell>
                <TableCell
                  sx={{ fontWeight: 500, fontSize: "16px", width: 110 }}
                >
                  {member.recipientType}
                </TableCell>
                <TableCell
                  sx={{ fontWeight: 500, fontSize: "16px", width: 110 }}
                >
                  {member.outreachOutcome}
                </TableCell>
                <TableCell
                  sx={{ fontWeight: 500, fontSize: "16px", width: 110 }}
                >
                  {member.programOutcome}
                </TableCell>
                <TableCell
                  sx={{ fontWeight: 500, fontSize: "16px", width: 110 }}
                >
                  {member.billableEvent}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </div>
  );
};

InvoiceReportingTable.propTypes = {
  loading: PropTypes.bool,
  error: PropTypes.string,
  results: PropTypes.array.isRequired,
  pageIndex: PropTypes.number.isRequired,
  totalRows: PropTypes.number.isRequired,
  computedTableHeight: PropTypes.number.isRequired,
  sortedInvoiceReporting: PropTypes.array.isRequired,
  sortConfig: PropTypes.object.isRequired,
  onSort: PropTypes.func.isRequired,
  tableContainerRef: PropTypes.object.isRequired,
  isMobile: PropTypes.bool.isRequired,
};

// --- Custom Hooks ---

const useInvoiceData = (appliedDateRange, pageSize = 30) => {
  const [invoiceReporting, setInvoiceReporting] = useState([]);
  const [pageIndex, setPageIndex] = useState(0);
  const [results, setResults] = useState([]);
  const [totalRows, setTotalRows] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const isFetchingMoreRef = useRef(false);
  const lastFetchSignatureRef = useRef(null);
  const { showLoading, hideLoading } = useLoading();

  const loadInitialData = useCallback(
    async () => {
      try {
        const { start: effectiveStart, end: effectiveEnd } =
          getEffectiveDateRange(appliedDateRange);

        const params = {
          startDate: format(effectiveStart, "yyyy-MM-dd"),
          endDate: format(effectiveEnd, "yyyy-MM-dd"),
          page: pageIndex,
          size: pageSize,
        };
        setLoading(true);

        const signature = JSON.stringify({
          ...params,
          startDate: format(effectiveStart, "yyyy-MM-dd"),
          endDate: format(effectiveEnd, "yyyy-MM-dd"),
        });

        if (lastFetchSignatureRef.current === signature) {
          setLoading(false);
          return;
        }
        lastFetchSignatureRef.current = signature;

        showLoading();
        const apiRes = await fetchInvoicingReporting(params);
        setError(null);
        const mapped = mapResponse(apiRes);

        if (pageIndex === 0) {
          setResults(mapped);
          setInvoiceReporting(mapped);
        } else {
          setResults((prev) => [...prev, ...mapped]);
          setInvoiceReporting((prev) => [...prev, ...mapped]);
        }
        setTotalRows(apiRes.totalElements || mapped.length);
      } catch (err) {
        console.error(err);
        setError("Failed to load data");
      } finally {
        setLoading(false);
        isFetchingMoreRef.current = false;
        hideLoading();
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      pageIndex,
      pageSize,
      appliedDateRange.startDate,
      appliedDateRange.endDate,
      showLoading,
      hideLoading,
    ],
  );

  useEffect(() => {
    loadInitialData();
  }, [loadInitialData]);

  useEffect(() => {
    setPageIndex(0);
    setResults([]);
    setInvoiceReporting([]);
    lastFetchSignatureRef.current = null;
  }, [appliedDateRange]);

  return {
    results,
    loading,
    error,
    setError,
    totalRows,
    pageIndex,
    setPageIndex,
    invoiceReporting,
    isFetchingMoreRef,
  };
};

const useDateRangeFilter = () => {
  const [dateRange, setDateRange] = useState({
    startDate: null,
    endDate: null,
  });
  const [appliedDateRange, setAppliedDateRange] = useState({
    startDate: null,
    endDate: null,
  });
  const [dateDialogOpen, setDateDialogOpen] = useState(false);
  const [endDateError, setEndDateError] = useState("");
  const [dateDialogWasApplied, setDateDialogWasApplied] = useState(false);

  const openDateDialog = () => {
    setDateDialogOpen(true);
    if (!dateDialogWasApplied) {
      setDateRange({ startDate: null, endDate: null });
      setEndDateError("");
    }
  };

  const closeDateDialog = () => {
    setDateDialogOpen(false);
    setDateRange({ startDate: null, endDate: null });
    setEndDateError("");
    setDateDialogWasApplied(false);
  };

  const handleDateChange = (date, type) => {
    setDateRange((prev) => ({ ...prev, [type]: date }));
  };

  const applyDateRange = () => {
    if (dateRange.startDate && dateRange.endDate && !endDateError) {
      setAppliedDateRange({ ...dateRange });
      setDateDialogWasApplied(true);
      setDateDialogOpen(false);
      return true;
    }
    return false;
  };

  return {
    dateRange,
    appliedDateRange,
    dateDialogOpen,
    endDateError,
    setEndDateError,
    openDateDialog,
    closeDateDialog,
    handleDateChange,
    applyDateRange,
    setAppliedDateRange,
    setDateRange,
  };
};

const useTableSort = (invoiceReporting) => {
  const [sortConfig, setSortConfig] = useState({ key: null, direction: null });

  const cycleSort = (key) => {
    setSortConfig((prev) => {
      if (prev.key !== key) return { key, direction: "asc" };
      if (prev.direction === "asc") return { key, direction: "desc" };
      if (prev.direction === "desc") return { key: null, direction: null };
      return { key, direction: "asc" };
    });
  };

  const sortedInvoiceReporting = useMemo(() => {
    if (!sortConfig.key || !sortConfig.direction) return invoiceReporting;
    return getSortedData(invoiceReporting, sortConfig);
  }, [invoiceReporting, sortConfig]);

  return { sortConfig, cycleSort, sortedInvoiceReporting };
};

const useTableLayout = (
  loading,
  isFetchingMoreRef,
  results,
  totalRows,
  setPageIndex,
) => {
  const tableContainerRef = useRef(null);
  const resultsContainerRef = useRef(null);
  const [computedTableHeight, setComputedTableHeight] = useState(0);
  const [containerScale, setContainerScale] = useState(1);

  useEffect(() => {
    const el = tableContainerRef.current;
    if (!el) return;
    const onScroll = () => {
      if (loading || isFetchingMoreRef.current) return;
      const canLoadMore = results.length < totalRows;
      if (!canLoadMore) return;
      const threshold = 100;
      if (el.scrollHeight - el.scrollTop - el.clientHeight < threshold) {
        isFetchingMoreRef.current = true;
        setPageIndex((prev) => prev + 1);
      }
    };
    el.addEventListener("scroll", onScroll);
    return () => el.removeEventListener("scroll", onScroll);
  }, [loading, results.length, totalRows, setPageIndex, isFetchingMoreRef]);

  useEffect(() => {
    const computeHeight = () => {
      const el = resultsContainerRef.current;
      if (!el) return;
      const vh = window.innerHeight || document.documentElement.clientHeight;
      const top = el.getBoundingClientRect().top;
      const isSmallLaptop =
        window.innerWidth <= 1440 && window.innerHeight <= 900;
      const extra = isSmallLaptop ? 24 : 0;
      const bottomPadding = isSmallLaptop ? 4 : 16;
      setComputedTableHeight(
        Math.max(200, Math.floor(vh - top - bottomPadding + extra)),
      );
    };
    computeHeight();
    window.addEventListener("resize", computeHeight);
    const ro = new ResizeObserver(() => computeHeight());
    try {
      ro.observe(document.body);
    } catch (e) {
      console.log(e);
    }
    return () => {
      window.removeEventListener("resize", computeHeight);
      ro.disconnect?.();
    };
  }, []);

  useEffect(() => {
    const inches = getScreenInches();
    setContainerScale(inches < 14 ? 0.9 : 1);
  }, []);

  return {
    tableContainerRef,
    resultsContainerRef,
    computedTableHeight,
    containerScale,
  };
};

const InvoiceReporting = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const location = useLocation();

  const {
    dateRange,
    appliedDateRange,
    dateDialogOpen,
    endDateError,
    setEndDateError,
    openDateDialog,
    closeDateDialog,
    handleDateChange,
    applyDateRange,
    setAppliedDateRange,
    setDateRange,
  } = useDateRangeFilter();

  const {
    results,
    loading,
    error,
    setError,
    totalRows,
    pageIndex,
    setPageIndex,
    invoiceReporting,
    isFetchingMoreRef,
  } = useInvoiceData(appliedDateRange);

  const { sortConfig, cycleSort, sortedInvoiceReporting } =
    useTableSort(invoiceReporting);

  const {
    tableContainerRef,
    resultsContainerRef,
    computedTableHeight,
    containerScale,
  } = useTableLayout(
    loading,
    isFetchingMoreRef,
    results,
    totalRows,
    setPageIndex,
  );

  const isPriorityOutreach = location.pathname.includes("priority-outreach");
  const isMemberMonitoring = location.pathname.includes("monitoring");

  const getCurrentSection = () => {
    if (isPriorityOutreach) return "priority-outreach";
    if (isMemberMonitoring) return "monitoring";
    return "members";
  };
  const [activeSection] = useState(getCurrentSection());
  const currentSection = activeSection ? SECTIONS[activeSection] : null;
  const sectionTitle = currentSection?.title || "";
  const sectionDescription = currentSection?.description || "";

  return (
    <Box sx={{ display: "flex", backgroundColor: "transparent" }}>
      <CssBaseline />
      <MainContent className="search-page">
        {dateDialogOpen && (
          <Box
            onClick={closeDateDialog}
            sx={{
              position: "fixed",
              inset: 0,
              backgroundColor: "rgba(0,0,0,0.35)",
              zIndex: 1200,
            }}
          />
        )}
        <div className="search-page-container">
          <div
            className="search-page-content"
            style={{
              transform: `scale(${containerScale})`,
              transformOrigin: "top left",
              width: containerScale < 1 ? "111%" : "100%",
              minHeight: "92vh",
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.5,
                minHeight: "55px",
                ml: 0,
                mt: 1,
                marginBottom: "-8px",
                fontSize: "18px",
                fontWeight: 600,
                lineHeight: "30px",
                letterSpacing: "0%",
                color: "#080A0E",
                "& a, & span:last-child": {
                  color: "#080A0E",
                  fontSize: "20px",
                  fontWeight: 600,
                  lineHeight: "30px",
                  letterSpacing: "0%",
                  textDecoration: "none",
                },
                "& a:hover": { textDecoration: "underline" },
                "& .breadcrumb-separator": {
                  margin: "0 4px",
                  color: "#080A0E",
                },
              }}
            >
              {BREADCRUMBS.map((breadcrumb, index) => (
                <React.Fragment
                  key={`breadcrumb-${breadcrumb.label || ""}-${breadcrumb.path || ""}-${index}`}
                >
                  {index > 0 && (
                    <span className="breadcrumb-separator">&gt;</span>
                  )}
                  {breadcrumb.path ? (
                    <a href={breadcrumb.path}>{breadcrumb.label}</a>
                  ) : (
                    <span>{breadcrumb.label}</span>
                  )}
                </React.Fragment>
              ))}
            </Box>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 2,
              }}
            >
              <Box sx={{ flex: 1 }}>
                <PageHeader
                  title={sectionTitle}
                  description={sectionDescription}
                />
              </Box>
              <Stack direction="row" spacing={1} marginRight={1}>
                <InvoiceExportButton
                  totalRows={totalRows}
                  appliedDateRange={appliedDateRange}
                  setError={setError}
                />
                <InvoiceDateFilterControl
                  appliedDateRange={appliedDateRange}
                  openDateDialog={openDateDialog}
                  closeDateDialog={closeDateDialog}
                  setAppliedDateRange={setAppliedDateRange}
                  setDateRange={setDateRange}
                  setPageIndex={setPageIndex}
                  totalRows={totalRows}
                />
                <InvoiceDateRangeDialog
                  open={dateDialogOpen}
                  onClose={closeDateDialog}
                  dateRange={dateRange}
                  onDateChange={handleDateChange}
                  endDateError={endDateError}
                  setEndDateError={setEndDateError}
                  onApply={() => {
                    if (applyDateRange()) {
                      setPageIndex(0);
                    }
                  }}
                />
              </Stack>
            </Box>

            <div className="search-results-container" ref={resultsContainerRef}>
              <InvoiceReportingTable
                loading={loading}
                error={error}
                results={results}
                pageIndex={pageIndex}
                totalRows={totalRows}
                computedTableHeight={computedTableHeight}
                sortedInvoiceReporting={sortedInvoiceReporting}
                sortConfig={sortConfig}
                onSort={cycleSort}
                tableContainerRef={tableContainerRef}
                isMobile={isMobile}
              />
            </div>
          </div>
        </div>
      </MainContent>
    </Box>
  );
};

export default InvoiceReporting;
